import type { NumberFormatConfig } from '#src/modules/numberFormat/types'

const parseFractionSection = (
  pattern: string
): { integerTemplate: string; decimal: string; fractionDigits: number } => {
  const trailingDecimalChar = pattern.charAt(pattern.length - 3)
  if (pattern.length >= 3 && pattern.endsWith('##') && trailingDecimalChar !== '#') {
    return {
      integerTemplate: pattern.slice(0, -3),
      decimal: trailingDecimalChar,
      fractionDigits: 2
    }
  }

  const fractionMatch = pattern.match(/^(.*)([^#])(#+)$/)
  if (fractionMatch) {
    const integerTemplate = fractionMatch[1] ?? ''
    const decimal = fractionMatch[2] ?? ''
    const hashRun = fractionMatch[3] ?? ''
    if (hashRun.length !== 3) {
      return {
        integerTemplate,
        decimal,
        fractionDigits: hashRun.length
      }
    }
  }

  return {
    integerTemplate: pattern,
    decimal: '',
    fractionDigits: 0
  }
}

const parseGroupSeparator = (integerTemplate: string): string => {
  const separators = new Set<string>()

  for (const char of integerTemplate) {
    if (char !== '#') {
      separators.add(char)
    }
  }

  if (separators.size > 1) {
    throw new Error(`Ambiguous group separators in number format pattern: ${integerTemplate}`)
  }

  return separators.size === 1 ? separators.values().next().value! : ''
}

export const parseNumberFormatPattern = (pattern: string): NumberFormatConfig => {
  const trimmed = pattern.trim()
  if (!trimmed) {
    throw new Error('Number format pattern must not be empty')
  }

  // Mask tokens only: `#` digit placeholders; `,` `.` group/decimal seps; whitespace (e.g. `# ###,##`).
  if (!/^[#.,\s]+$/.test(trimmed)) {
    throw new Error(`Invalid characters in number format pattern: ${pattern}`)
  }

  const { integerTemplate, decimal, fractionDigits } = parseFractionSection(trimmed)
  const group = parseGroupSeparator(integerTemplate)

  return {
    pattern: trimmed,
    group,
    decimal,
    fractionDigits
  }
}
